import { StrictMode, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Outlet, RouterProvider, useLocation, useRouteError, type RouteObject } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import { Banner, Btn, ModeProvider, ToastProvider } from '@infinix/ui';
import { CATALOG, visibleApps, type AppManifest } from '@infinix/registry';
import { SessionProvider, useSession } from './session';
import { CrumbsProvider } from './crumbs';
import { BadgesProvider } from './badges';
import { NoAccess, Shell } from './Shell';
import { isLeaving, leaveTo } from './leave';

/**
 * Every Nest app is its own deployable (docs/APP-CATALOG.md §3). This file is the only thing an app's
 * entry point calls: it mounts the shared providers, the shell chrome and the app's own routes.
 */

export interface LocalApp {
  manifest: AppManifest;
  routes: RouteObject[];
}

const owns = (app: AppManifest, path: string) => path === app.basePath || path.startsWith(`${app.basePath}/`);

/** Signed-out visitors go to the sign-in page (the account app) and come back here afterwards. */
function RequireSession({ children }: { children: ReactNode }) {
  const s = useSession();
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    if (s.status === 'signed-out' && !isLeaving()) leaveTo(`/login?next=${encodeURIComponent(pathname + search + hash)}`, { replace: true });
  }, [s.status, pathname, search, hash]);
  if (s.status === 'error') return <SessionUnavailable onRetry={s.retry} />;
  return s.status === 'signed-in' ? <>{children}</> : null;
}

/**
 * Direct URL access to an app the person can't use shows a clear message (the app's backend refuses it too). A customer
 * who reaches a Nest staff app (an old bookmark, a shared link) is simply taken to their own home: the staff console
 * isn't theirs to see or explain.
 */
function AppGate({ app }: { app: AppManifest }) {
  const s = useSession();
  const mine = visibleApps(s.identity, CATALOG, s.view?.staffTenantAccess);
  if (mine.some((a) => a.id === app.id)) return <Outlet />;
  if (app.audience === 'staff' && s.audience !== 'staff') return <ToHome />;
  return <NoAccess what={app.name} />;
}

function ToHome() {
  useEffect(() => { leaveTo('/', { replace: true }); }, []);
  return null;
}

/** A page that failed to render: say so plainly and offer a way on, instead of the router's developer screen. */
function PageCrashed() {
  const err = useRouteError();
  useEffect(() => { console.error('Nest page error', err); }, [err]);
  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 8, px: 2 }}>
      <Banner tone="danger" action={<Btn onClick={() => window.location.reload()}>Reload</Btn>}>
        This page ran into a problem and couldn't open. Reload to try again; if it keeps happening, tell Nest support what you were doing.
      </Banner>
    </Box>
  );
}

function SessionUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8, px: 2 }}>
      <Banner tone="warning" action={<Btn onClick={onRetry}>Try again</Btn>}>Nest can't check your sign-in right now. Try again in a moment.</Banner>
    </Box>
  );
}

/**
 * Any path this app doesn't own belongs to another app (or the sign-in/landing pages): hand over with a page
 * load. Client links and `navigate()` into other apps arrive here, so app code never needs to know which
 * paths live in which deployable.
 */
function CrossApp({ local }: { local: AppManifest[] }) {
  const { pathname, search, hash } = useLocation();
  const mine = local.some((a) => owns(a, pathname));
  useEffect(() => {
    if (!mine && !isLeaving()) leaveTo(pathname + search + hash, { replace: true });
  }, [mine, pathname, search, hash]);
  return mine ? <NoAccess what="this page" /> : null;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

/** Shared providers + router. The account app (sign-in, landing) uses this directly; console apps use mountConsoleApp. */
export function mountRoutes(routes: RouteObject[], opts: { wrap?: (children: ReactNode) => ReactNode } = {}) {
  const router = createBrowserRouter(routes);
  const app = <RouterProvider router={router} />;
  const root = document.getElementById('root');
  if (!root) throw new Error('#root missing from index.html');
  createRoot(root).render(
    <StrictMode>
      <ModeProvider>
        <ToastProvider>
          <QueryClientProvider client={queryClient}>
            <SessionProvider>
              <CrumbsProvider>
                <BadgesProvider>{opts.wrap ? opts.wrap(app) : app}</BadgesProvider>
              </CrumbsProvider>
            </SessionProvider>
          </QueryClientProvider>
        </ToastProvider>
      </ModeProvider>
    </StrictMode>,
  );
}

/**
 * Mount a console app: the shell chrome around this deployable's own app(s). Only the routes passed here are
 * bundled; the launcher still lists every app in the catalog.
 */
export function mountConsoleApp({ apps, wrap, outside = [] }: { apps: LocalApp[]; wrap?: (children: ReactNode) => ReactNode; outside?: RouteObject[] }) {
  const local = apps.map((a) => a.manifest);
  mountRoutes(
    [
      // Pages without the suite chrome (sign-in, landing) in deployables that own them.
      ...outside,
      {
        path: '/',
        element: (
          <RequireSession>
            <Shell local={local} />
          </RequireSession>
        ),
        errorElement: <PageCrashed />,
        children: [
          ...apps.map((a) => ({ path: a.manifest.basePath.replace(/^\//, ''), element: <AppGate app={a.manifest} />, children: a.routes })),
          { path: '*', element: <CrossApp local={local} /> },
        ],
      },
    ],
    wrap ? { wrap } : {},
  );
}
